import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ReferralProvider } from "@/contexts/ReferralContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import CookieConsent from "@/components/CookieConsent";
import CareerPopup from "@/components/CareerPopup";
import { AnimatePresence } from "framer-motion";

// Direct import only for Index (critical path)
import Index from "./pages/Index";

// Lazy load all other pages for better FCP/LCP
const Approach = lazy(() => import("./pages/Approach"));
const About = lazy(() => import("./pages/About"));
const Auth = lazy(() => import("./pages/Auth"));
const JobKarriere = lazy(() => import("./pages/JobKarriere"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Cases = lazy(() => import("./pages/Cases"));
const CaseDetail = lazy(() => import("./pages/CaseDetail"));
const Brandskader = lazy(() => import("./pages/Brandskader"));
const AndreSkader = lazy(() => import("./pages/AndreSkader"));
const Byggestyring = lazy(() => import("./pages/Byggestyring"));
const Forsikringsopgoerelser = lazy(() => import("./pages/Forsikringsopgoerelser"));
const Ejerskifteforsikring = lazy(() => import("./pages/Ejerskifteforsikring"));
const Myndighedskontakt = lazy(() => import("./pages/Myndighedskontakt"));
const NedrivningSaneringGenopbygning = lazy(() => import("./pages/NedrivningSaneringGenopbygning"));
const RenoveringOmbygning = lazy(() => import("./pages/RenoveringOmbygning"));
const IndboDriftstab = lazy(() => import("./pages/IndboDriftstab"));
const Samarbejdspartnere = lazy(() => import("./pages/Samarbejdspartnere"));
const Privatlivspolitik = lazy(() => import("./pages/Privatlivspolitik"));
const Cookiepolitik = lazy(() => import("./pages/Cookiepolitik"));
const Handelsbetingelser = lazy(() => import("./pages/Handelsbetingelser"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages - lazy loaded
const CasesOverview = lazy(() => import("./pages/admin/CasesOverview"));
const CaseEditor = lazy(() => import("./pages/admin/CaseEditor"));
const BlogPostEditor = lazy(() => import("./pages/admin/BlogPostEditor"));
const ServicesEditor = lazy(() => import("./pages/admin/ServicesEditor"));
const TeamEditor = lazy(() => import("./pages/admin/TeamEditor"));
const JobsEditor = lazy(() => import("./pages/admin/JobsEditor"));
const ReferralStats = lazy(() => import("./pages/admin/ReferralStats"));

const queryClient = new QueryClient();

// Minimal fallback for lazy loaded pages
const PageLoader = () => <div className="min-h-screen bg-background" />;

// Prefetch popular pages after initial load
const usePrefetchPages = () => {
  useEffect(() => {
    const prefetch = () => {
      // Prefetch most visited pages
      import("./pages/Cases");
      import("./pages/About");
      import("./pages/Brandskader");
      import("./pages/Approach");
    };
    
    // Use requestIdleCallback for non-blocking prefetch
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetch, { timeout: 3000 });
    } else {
      setTimeout(prefetch, 2000);
    }
  }, []);
};

const AnimatedRoutes = () => {
  const location = useLocation();
  usePrefetchPages();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<PageLoader />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/tilgang" element={<PageTransition><Approach /></PageTransition>} />
          <Route path="/om-os" element={<PageTransition><About /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/job-karriere" element={<PageTransition><JobKarriere /></PageTransition>} />
          <Route path="/job-karriere/:id" element={<PageTransition><JobDetail /></PageTransition>} />
          <Route path="/cases" element={<PageTransition><Cases /></PageTransition>} />
          <Route path="/cases/:id" element={<PageTransition><CaseDetail /></PageTransition>} />
          <Route path="/brand-vand-storm-sneskader" element={<PageTransition><Brandskader /></PageTransition>} />
          <Route path="/brandskader" element={<Navigate to="/brand-vand-storm-sneskader" replace />} />
          <Route path="/andre-skader" element={<PageTransition><AndreSkader /></PageTransition>} />
          <Route path="/byggestyring" element={<PageTransition><Byggestyring /></PageTransition>} />
          <Route path="/forsikringsopgoerelser" element={<PageTransition><Forsikringsopgoerelser /></PageTransition>} />
          <Route path="/ejerskifteforsikring" element={<PageTransition><Ejerskifteforsikring /></PageTransition>} />
          <Route path="/myndighedskontakt" element={<PageTransition><Myndighedskontakt /></PageTransition>} />
          <Route path="/nedrivning-sanering-genopbygning" element={<PageTransition><NedrivningSaneringGenopbygning /></PageTransition>} />
          <Route path="/renovering-ombygning" element={<PageTransition><RenoveringOmbygning /></PageTransition>} />
          <Route path="/indbo-driftstab" element={<PageTransition><IndboDriftstab /></PageTransition>} />
          <Route path="/samarbejdspartnere" element={<PageTransition><Samarbejdspartnere /></PageTransition>} />
          <Route path="/privatlivspolitik" element={<PageTransition><Privatlivspolitik /></PageTransition>} />
          <Route path="/cookiepolitik" element={<PageTransition><Cookiepolitik /></PageTransition>} />
          <Route path="/handelsbetingelser" element={<PageTransition><Handelsbetingelser /></PageTransition>} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <Navigate to="/admin/cases" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cases"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <CasesOverview />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cases/new"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <CaseEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cases/:id/edit"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <CaseEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/new"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <BlogPostEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/:id/edit"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <BlogPostEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/services"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <ServicesEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/team"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <TeamEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/jobs"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <JobsEditor />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/referral-stats"
          element={
            <ProtectedRoute requireAdmin>
              <Suspense fallback={<PageLoader />}>
                <ReferralStats />
              </Suspense>
            </ProtectedRoute>
          }
        />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <ReferralProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <AnimatedRoutes />
              <CookieConsent />
              <CareerPopup />
            </BrowserRouter>
          </ReferralProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
